import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import LabelType from '@/Controls/Label';
import CheckBoxViewModel from '@/ViewModels/Controls/CheckBoxViewModel';
import Loader from '@/Utility/Loader';

export default class CheckBox extends Control {

    protected Label = ComponentManager.getControl<typeof LabelType>('Label');
    protected jQueryControl: JQuery;
    protected label: LabelType;

    public constructor(protected viewModel: CheckBoxViewModel, protected element?: JQuery) {
        super(viewModel, element);        

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div>');

        if (this.jQueryControl.children('input[type="checkbox"]').length === 0) {
            this.jQueryControl.append('<input type="checkbox">');
        }

        if (this.jQueryControl.children('div.element').length === 0) {
            this.jQueryControl.append('<div class="element">');
        }

        this.widget(null);

        return this.jQueryControl;
    }

    protected onWidgetCreatedCallback(widget: null): void {
        this.applyBindings(this.jQueryControl, {
            controlName: 'checkbox',
            loader: this.viewModel.isBusy,
            controlIdent: this.identifier,
            tooltip: this.viewModel.tooltip,
            invalid: this.viewModel.invalidationText,
            visible: this.viewModel.isVisible,
            styles: this.definePureComputed({
                read: () => {
                    let styles = ['default'];

                    return styles;
                }
            }),
            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    if (this.viewModel.isValid() === true) {
                        states.push('valid');
                    } else {
                        states.push('invalid');
                    }

                    if (this.viewModel.isEnabled() === true) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    return states;
                }
            }),
            attr: {
                'data-value': this.viewModel.value,
                'data-checked': this.viewModel.isChecked
            },
            hotkey: {
                shortcut: 'space',
                handler: (e: ExtendedKeyboardEvent) => {
                    this.viewModel.toggleIfEnabled();
                    e.stopPropagation();
                    e.preventDefault();
                },
                isGlobal: false
            }
        });

        this.applyBindings(this.jQueryControl.find('input'), {
            checked: this.viewModel.isChecked,
            enable: this.viewModel.isEnabled,
            attr: {
                id: this.identifier
            }
        });

        this.applyBindings(this.jQueryControl.find('.element'), {
            locator: this.viewModel.locator,
            focus: this.viewModel.isFocusable,
            html: this.defineComputed(() => {
                return this.viewModel.isChecked() ? '&#10003;' : '';
            })
        });

        this.addEventHandler('click', this.jQueryControl, () => {
            this.viewModel.toggleIfEnabled();
        }, '.element');

        if (this.viewModel.label != null) {
            this.label = new this.Label(this.viewModel.label, $('<label for="' + this.identifier + '">').appendTo(this.jQueryControl));
            const tuple = this.label.getTuple();
            this.addEventHandler('click', tuple.control, (event: JQuery.ClickEvent) => {
                if (event.target.nodeName === 'LABEL' || event.target.className === 'label-text') {
                    //Das Standardverhalten des Browsers wird unterdrückt, sodass stattdessen unsere eigene Logik beim Click ausgeführt wird.
                    //Wie weiter unten ersichtlich, kann hier auch ein Custom Click Event verwendet werden.
                    event.preventDefault();
                }
                this.viewModel.toggleIfEnabled();
            });
        }

        if (this.viewModel.onClick != null) {
            this.addEventHandler('click', this.jQueryControl, (event: JQuery.Event) => {
                if (this.viewModel.isEnabled()) {
                    this.viewModel.onClick(this.viewModel.isChecked(), this.viewModel.value());
                }
            }, 'label, .element');
        }
    }

    public addLabelReference(label: JQuery): JQuery {
        let id = this.identifier + '-input';
        this.getControl().find('input').attr('id', id);
        label.attr('for', id);
        return label;
    }
}

ComponentManager.setControl('CheckBox', CheckBox, 'RADPresentation.HTMLClient.DefaultViewExtensions');

// Sonderfall:
// Ein Klick auf das Label der Checkbox löst im Browser intern ein Klick auf die Checkbox aus.
// Durch den Labelklick ist aber bereits der Loader geöffnet, welcher den internen Klick auf die Checkbox verhindert.
Loader.getInstance().registerPreventUserActionsException((e) => {
    let id = (e.target as HTMLElement).id;
    if (id.startsWith('CheckBox') && id.endsWith('-input'))
        return false;

    return true;
});